import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { generateEndpoints, getGeneratedEndpoints, getDatabaseStatus, enableDynamicEndpoint, disableDynamicEndpoint, downloadEndpointsDoc } from '../services/api';
import { Webhook, Copy, Play, Search, X, Download, Database } from 'lucide-react';
import { formatLocalTime } from '../utils/timezone';
import { copyTextToClipboard } from '../utils/clipboard';

export const ApiEndpoints = () => {
    const { t } = useTranslation(['page-api-endpoints', 'common']);
    const [searchParams] = useSearchParams();
    const { selectedDatabase, databases, setSelectedDatabase } = useDatabase();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [endpoints, setEndpoints] = useState([]);
    const [endpointsExist, setEndpointsExist] = useState(false);
    const [enableAll, setEnableAll] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [endpointStates, setEndpointStates] = useState({});
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [testParams, setTestParams] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [showResultView, setShowResultView] = useState('table'); // 'table' or 'json'
    const [selectedValueType, setSelectedValueType] = useState('all'); // Filter by value type
    const [testingEndpoint, setTestingEndpoint] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(false);

    const dbId = searchParams.get('db') || selectedDatabase?.id;

    // Filter databases to only show those with schema analysis completed
    const analyzedDatabases = databases.filter(db => 
        db.status && db.status.toLowerCase() === 'analyzed'
    );

    const handleDatabaseChange = (e) => {
        const dbId = e.target.value;
        const db = databases.find(db => db.id === parseInt(dbId));
        if (db) {
            setSelectedDatabase(db);
            // No toast notification for database switch
        }
    };

    useEffect(() => {
        if (dbId) {
            fetchEndpoints();
            // Also fetch the current enabled status for the database
            fetchEnabledStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbId]);

    const fetchEnabledStatus = async () => {
        try {
            const res = await getDatabaseStatus();
            const allDatabases = res.data?.data?.enabled_databases || [];
            const isDatabaseEnabled = allDatabases.some(db => db.database_id === parseInt(dbId));
            setEnableAll(isDatabaseEnabled);
        } catch (error) {
            console.error('Failed to fetch enabled status:', error);
        }
    };

    const fetchEndpoints = async () => {
        if (!dbId) return;
        setLoading(true);
        try {
            // First, get the generated endpoints (these are the definitions)
            const genRes = await getGeneratedEndpoints(dbId);
            const endpointsData = genRes.data?.data?.endpoints || [];
            setEndpoints(endpointsData);

            // Check if endpoints exist for this database
            setEndpointsExist(endpointsData && endpointsData.length > 0);

            // Then, get the database status to determine which ones are actually active
            try {
                const statusRes = await getDatabaseStatus();
                const enabledDatabases = statusRes.data?.data?.enabled_databases || [];
                const isCurrentDatabaseEnabled = enabledDatabases.some(db => db.database_id === parseInt(dbId));

                // Initialize endpoint states based on whether the database is enabled
                const states = {};
                endpointsData.forEach(ep => {
                    states[ep.id] = isCurrentDatabaseEnabled;
                });
                setEndpointStates(states);
            } catch (statusError) {
                console.error('Failed to fetch database status:', statusError);
                // If we can't get the status, default to false
                const states = {};
                endpointsData.forEach(ep => {
                    states[ep.id] = false;
                });
                setEndpointStates(states);
            }
        } catch (error) {
            console.error('Failed to fetch endpoints:', error);
            showToast(t('page-api-endpoints:toast.fetchFailed'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!dbId) return;
        setGenerating(true);
        try {
            await generateEndpoints(dbId);
            // No toast notification - silently proceed
            fetchEndpoints();
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-api-endpoints:toast.generateFailed'), 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!dbId) {
            showToast(t('page-api-endpoints:stats.noDatabaseSelected'), 'error');
            return;
        }

        setDownloadingReport(true);
        try {
            const response = await downloadEndpointsDoc(dbId);

            // Create a blob from the response data
            const blob = new Blob([response.data], { type: 'text/markdown' });

            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `endpoints_document_${selectedDatabase?.name || 'database'}.md`);

            // Trigger the download
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // No toast notification - download is silent
        } catch (error) {
            console.error('Error downloading report:', error);
            showToast(error.response?.data?.detail || t('page-api-endpoints:toast.downloadFailed'), 'error');
        } finally {
            setDownloadingReport(false);
        }
    };


    const copyToClipboard = async (text) => {
        try {
            await copyTextToClipboard(text);
        } catch {
            // Silent fail for path copy
        }
    };


    const toggleEnableAll = async () => {
        const newState = !enableAll;
        setEnableAll(newState);

        try {
            if (newState) {
                // Enable all endpoints for this database
                await enableDynamicEndpoint(dbId);
            } else {
                // Disable all endpoints for this database
                await disableDynamicEndpoint(dbId);
            }

            // Refresh the endpoint states after successful toggle
            setTimeout(() => {
                fetchEndpoints(); // Refresh to get updated states
            }, 500); // Small delay to allow backend to process the change
        } catch (error) {
            console.error('Failed to toggle endpoints:', error);
            // Revert the UI state if the API call failed
            setEnableAll(!newState);
            showToast(t('page-api-endpoints:toast.toggleFailed', {
                action: newState ? t('page-api-endpoints:toast.actionEnable') : t('page-api-endpoints:toast.actionDisable'),
                message: error.message,
            }), 'error');
        }
    };

    // Extract unique value types from endpoints
    const valueTypes = React.useMemo(() => {
        const types = new Set();
        endpoints.forEach(endpoint => {
            // Value type can be in generated_query.value_type or directly on endpoint
            const valueType = endpoint.generated_query?.value_type || endpoint.value_type || 'General';
            types.add(valueType);
        });
        return Array.from(types).sort();
    }, [endpoints]);

    const filteredEndpoints = endpoints.filter(endpoint => {
        // Filter by search query
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = (
                endpoint.name?.toLowerCase().includes(searchLower) ||
                endpoint.path?.toLowerCase().includes(searchLower) ||
                endpoint.description?.toLowerCase().includes(searchLower)
            );
            if (!matchesSearch) return false;
        }

        // Filter by value type
        if (selectedValueType !== 'all') {
            // Value type can be in generated_query.value_type or directly on endpoint
            const endpointValueType = endpoint.generated_query?.value_type || endpoint.value_type || 'General';
            if (endpointValueType !== selectedValueType) return false;
        }

        return true;
    });

    const openTestModal = (endpoint) => {
        setSelectedEndpoint(endpoint);

        // Initialize testParams with default values from endpoint parameters
        const initialParams = {};

        // Handle path parameters from the endpoint path
        if (endpoint.path) {
            const pathRegex = /{([^}]+)}/g;
            let match;
            pathRegex.lastIndex = 0; // Reset regex state to ensure consistent matching
            while ((match = pathRegex.exec(endpoint.path)) !== null) {
                const paramName = match[1];
                
                // Look for this parameter in endpoint parameters to get default value
                const paramDef = (endpoint.parameters || []).find(p => p.name === paramName) || {};
                                
                initialParams[paramName] = paramDef?.default ?? paramDef?.example ?? '';
            }
        }

        // Handle query parameters from endpoint - also check for duplicates
        const allParams = [
            ...(endpoint.parameters || [])
        ].filter((param, index, arr) => {
            // Remove duplicates by parameter name
            const firstIndex = arr.findIndex(p => p.name === param.name);
            return firstIndex === index;
        });

        allParams.forEach(param => {
            // Only add query parameters (not path parameters) to initialParams
            if (!endpoint.path || !endpoint.path.includes(`{${param.name}}`)) {
                if (!initialParams.hasOwnProperty(param.name)) {
                    initialParams[param.name] = param.default ?? param.example ?? '';
                }
            }
        });

        setTestParams(initialParams);
        setTestResult(null); // Clear previous results when opening modal
        setTestModalOpen(true);
    };

    const closeTestModal = () => {
        setTestModalOpen(false);
        setSelectedEndpoint(null);
        setTestParams({});
        setTestResult(null);
    };

    const handleParamChange = (paramName, value) => {
        setTestParams(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const handleTestEndpoint = async () => {
        if (!selectedEndpoint) return;

        setTestingEndpoint(true);
        try {
            // Construct the URL with path parameters substituted
            let url = selectedEndpoint.path;

            // Replace path parameters with values from testParams
            const pathParamNames = [];
            if (selectedEndpoint.path) {
                const pathRegex = /{([^}]+)}/g;
                let match;
                pathRegex.lastIndex = 0; // Reset regex state
                while ((match = pathRegex.exec(selectedEndpoint.path)) !== null) {
                    pathParamNames.push(match[1]);
                }
            }

            // Replace path parameters in the URL
            pathParamNames.forEach(paramName => {
                if (testParams.hasOwnProperty(paramName) && testParams[paramName] !== undefined && testParams[paramName] !== null && testParams[paramName] !== '') {
                    const regex = new RegExp(`{${paramName}}`, 'g');
                    url = url.replace(regex, encodeURIComponent(testParams[paramName]));
                } else {
                    // If a required path parameter is missing, throw an error
                    throw new Error(t('page-api-endpoints:toast.missingPathParam', { param: paramName }));
                }
            });

            // Separate query parameters (those not in the path)
            const queryParamKeys = Object.keys(testParams).filter(
                key => !pathParamNames.includes(key)
            );

            if (queryParamKeys.length > 0) {
                const queryParams = new URLSearchParams();
                queryParamKeys.forEach(key => {
                    // Only add parameters that have values
                    if (testParams[key] !== undefined && testParams[key] !== null && testParams[key] !== '') {
                        queryParams.append(key, testParams[key]);
                    }
                });

                url += '?' + queryParams.toString();
            }

            // Make the API call
            const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8008'}${url}`;
            console.log('Making API call to:', apiUrl);

            const response = await fetch(apiUrl, {
                method: selectedEndpoint.method || 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();
            console.log('Endpoint result:', result);

            // Store the result and show the result view
            setTestResult(result);
            setShowResultView('table'); // Default to table view
        } catch (error) {
            console.error('Error testing endpoint:', error);
            showToast(t('page-api-endpoints:toast.testEndpointError', { message: error.message }), 'error');
        } finally {
            setTestingEndpoint(false);
        }
    };

    return (
        <div className="page-content">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="page-title">{t('page-api-endpoints:header.title')}</h1>
                        <p className="page-subtitle">{t('page-api-endpoints:header.subtitle')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={dbId || ''}
                            onChange={handleDatabaseChange}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                        >
                            <option value="">{t('page-api-endpoints:header.selectDatabase')}</option>
                            {analyzedDatabases.map((db) => (
                                <option key={db.id} value={db.id}>{db.name}</option>
                            ))}
                        </select>
                        {endpointsExist && (
                            <button
                                onClick={handleDownloadReport}
                                disabled={!dbId || downloadingReport}
                                className={`px-4 py-2 ${
                                    !dbId || downloadingReport ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                                } text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                            >
                                <Download className="w-4 h-4" />
                                {downloadingReport ? t('page-api-endpoints:header.downloading') : t('page-api-endpoints:header.downloadReport')}
                            </button>
                        )}
                        <button
                            onClick={handleGenerate}
                            disabled={generating || !dbId || endpointsExist}
                            className={`px-4 py-2 ${
                                endpointsExist ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                            } text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                        >
                            <Webhook className="w-4 h-4" />
                            {generating ? t('page-api-endpoints:header.generating') : endpointsExist ? t('page-api-endpoints:header.endpointsGenerated') : t('page-api-endpoints:header.generateEndpoints')}
                        </button>
                    </div>
                </div>

                {/* Stats and Controls */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('page-api-endpoints:stats.totalEndpoints')}</p>
                                <p className="text-2xl font-bold text-gray-900">{endpoints.length}</p>
                            </div>
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                <Webhook className="w-5 h-5 text-primary-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('page-api-endpoints:stats.database')}</p>
                                <p className="text-lg font-semibold text-gray-900 truncate">{databases.find(db => db.id === parseInt(dbId))?.name || t('page-api-endpoints:stats.noDatabaseSelected')}</p>
                            </div>
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Database className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('page-api-endpoints:stats.enableEndpoints')}</p>
                                <p className={`text-sm font-medium ${enableAll ? 'text-green-600' : 'text-gray-500'}`}>
                                    {enableAll ? t('common:enabled') : t('common:disabled')}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={enableAll}
                                    onChange={toggleEnableAll}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                    <div className="flex gap-4">
                        {/* Search Input */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('page-api-endpoints:filters.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        {/* Value Type Filter */}
                        <div className="relative min-w-[200px]">
                            <select
                                value={selectedValueType}
                                onChange={(e) => setSelectedValueType(e.target.value)}
                                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white cursor-pointer"
                            >
                                <option value="all">{t('page-api-endpoints:filters.allValueTypes')}</option>
                                {valueTypes.map((valueType) => (
                                    <option key={valueType} value={valueType}>
                                        {valueType}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Active filter indicator */}
                    {(selectedValueType !== 'all' || searchQuery) && (
                        <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('page-api-endpoints:filters.activeFilters')}</span>
                            {selectedValueType !== 'all' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                                    {t('page-api-endpoints:filters.valueType')} {selectedValueType}
                                    <button
                                        onClick={() => setSelectedValueType('all')}
                                        className="hover:bg-primary-200 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {searchQuery && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                                    {t('page-api-endpoints:filters.search')} {searchQuery}
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="hover:bg-primary-200 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    setSelectedValueType('all');
                                    setSearchQuery('');
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 underline"
                            >
                                {t('page-api-endpoints:filters.clearAll')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Endpoints List */}
                {!dbId ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-api-endpoints:emptyStates.noDatabaseTitle')}</h3>
                        <p className="text-gray-600 mb-3">{t('page-api-endpoints:emptyStates.noDatabaseBody')}</p>
                        <p className="text-sm text-gray-500">
                            {t('page-api-endpoints:emptyStates.schemaAnalysisHint')}
                        </p>
                    </div>
                ) : loading ? (
                    <LoadingSpinner />
                ) : filteredEndpoints.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <p className="text-gray-500 text-sm mb-2">
                            {endpoints.length === 0
                                ? t('page-api-endpoints:emptyStates.noEndpointsGetStarted')
                                : searchQuery || selectedValueType !== 'all'
                                    ? t('page-api-endpoints:emptyStates.noEndpointsMatchFilters')
                                    : t('page-api-endpoints:emptyStates.noEndpointsAvailable')
                            }
                        </p>
                        {(searchQuery || selectedValueType !== 'all') && (
                            <button
                                onClick={() => {
                                    setSelectedValueType('all');
                                    setSearchQuery('');
                                }}
                                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                                {t('page-api-endpoints:emptyStates.clearAllFilters')}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Group by value_type first, then by category */}
                        {Object.entries(
                            filteredEndpoints.reduce((acc, endpoint) => {
                                const value_type = endpoint.generated_query?.value_type || endpoint.value_type || 'general';
                                if (!acc[value_type]) acc[value_type] = {};

                                const category = endpoint.generated_query?.category || endpoint.category || 'general';
                                if (!acc[value_type][category]) acc[value_type][category] = [];

                                acc[value_type][category].push(endpoint);
                                return acc;
                            }, {})
                        ).map(([value_type, categories]) => (
                            <div key={value_type} className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 capitalize">{t('page-api-endpoints:list.insightsGroup', { type: value_type })}</h2>

                                {Object.entries(categories).map(([category, categoryEndpoints]) => (
                                    <div key={category} className="ml-4 space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800 capitalize">{t('page-api-endpoints:list.analyticsGroup', { category })}</h3>
                                        <p className="text-sm text-gray-600 ml-2">{t('page-api-endpoints:list.endpointsAvailableCount', { count: categoryEndpoints.length })}</p>

                                        <div className="space-y-3 ml-2">
                                            {categoryEndpoints.map((endpoint) => (
                                                <div key={endpoint.id} className="bg-white rounded-lg border border-gray-200 p-6">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                                    endpoint.method === 'POST' ? 'bg-primary-100 text-primary-700' :
                                                                    endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                                                                    endpoint.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                                                                    'bg-green-100 text-green-700'
                                                                }`}>
                                                                    {endpoint.method || 'GET'}
                                                                </span>
                                                                <h4 className="font-semibold text-gray-900">{endpoint.name}</h4>
                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${endpointStates[endpoint.id] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                    {endpointStates[endpoint.id] ? t('common:enabled') : t('common:disabled')}
                                                                </span>
                                                            </div>
                                                            <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono block mb-2">
                                                                {endpoint.path || '/api/endpoint'}
                                                            </code>
                                                            <p className="text-sm text-gray-600 mb-3">
                                                                {endpoint.documentation?.description || endpoint.description || t('page-api-endpoints:list.endpointDescriptionFallback')}
                                                            </p>
                                                            <div className="flex items-center gap-4 text-xs">
                                                                <div className="bg-primary-50 px-3 py-1 rounded">
                                                                    <span className="text-primary-900">
                                                                        <span className="font-semibold">{t('page-api-endpoints:list.idLabel')}</span> {endpoint.id}
                                                                    </span>
                                                                </div>
                                                                <div className="bg-primary-50 px-3 py-1 rounded">
                                                                    <span className="text-primary-900">
                                                                        <span className="font-semibold">{t('page-api-endpoints:list.databaseLabel')}</span> {selectedDatabase?.name || t('page-api-endpoints:stats.noDatabaseSelected')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 text-xs text-gray-500">
                                                                {t('page-api-endpoints:list.createdLabel')} {formatLocalTime(endpoint.created_at)}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 ml-4">
                                                            <button
                                                                onClick={() => copyToClipboard(endpoint.path || '')}
                                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                                                title={t('page-api-endpoints:list.copyEndpointTooltip')}
                                                            >
                                                                <Copy className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => openTestModal(endpoint)}
                                                                className={`p-2 rounded ${endpointStates[endpoint.id] ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 cursor-not-allowed'}`}
                                                                title={endpointStates[endpoint.id] ? t('page-api-endpoints:list.testEndpointTooltip') : t('page-api-endpoints:list.enableToTestTooltip')}
                                                                disabled={!endpointStates[endpoint.id]}
                                                            >
                                                                <Play className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}

            {/* Test Endpoint Modal */}
            {testModalOpen && selectedEndpoint && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] min-h-[40vh] h-auto overflow-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold">{t('page-api-endpoints:modal.testEndpoint')}</h3>
                                <button
                                    onClick={closeTestModal}
                                    className="p-1 text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            
                            <div className="mb-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        selectedEndpoint.method === 'POST' ? 'bg-primary-100 text-primary-700' :
                                        selectedEndpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                                        selectedEndpoint.method === 'DELETE' ? 'bg-red-100 text-red-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                        {selectedEndpoint.method || 'GET'}
                                    </span>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{selectedEndpoint.name}</h4>
                                        <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono">
                                            {selectedEndpoint.path}
                                        </code>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600">{selectedEndpoint.documentation?.description || selectedEndpoint.description}</p>
                            </div>
                            
                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-900 mb-2">{t('page-api-endpoints:modal.parameters')}</h4>
                                
                                <div className="space-y-3">
                                    {/* Path parameters from the endpoint path */}
                                    {(() => {
                                        const pathParams = [];
                                        if (selectedEndpoint.path) {
                                            const pathRegex = /{([^}]+)}/g;
                                            let match;
                                            pathRegex.lastIndex = 0; // Reset regex state to ensure consistent matching
                                            while ((match = pathRegex.exec(selectedEndpoint.path)) !== null) {
                                                pathParams.push(match[1]);
                                            }
                                        }

                                        return (
                                            <>
                                                {pathParams.length > 0 && (
                                                    <div className="pb-4 border-b border-gray-200">
                                                        <h5 className="font-medium text-gray-800 mb-3">{t('page-api-endpoints:modal.pathParameters')}</h5>
                                                        <div className="space-y-3">
                                                            {pathParams.map((paramName, idx) => {
                                                                // Find the parameter definition in the endpoint parameters
                                                                const paramDef = (selectedEndpoint.parameters || []).find(p => p.name === paramName) || {};

                                                                // Determine input type - use date picker for date types
                                                                const inputType = paramDef.type === 'date' || paramDef.type === 'datetime' ? 'date' : 'text';

                                                                return (
                                                                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                                        <div className="md:col-span-3">
                                                                            <label className="block text-sm font-medium text-gray-700">
                                                                                {paramName}
                                                                                {paramDef.required && <span className="text-red-500"> *</span>}
                                                                            </label>
                                                                        </div>
                                                                        <div className="md:col-span-6">
                                                                            <input
                                                                                type={inputType}
                                                                                value={testParams[paramName] ?? paramDef.default ?? paramDef.example ?? ''}
                                                                                onChange={(e) => handleParamChange(paramName, e.target.value)}
                                                                                placeholder={paramDef.example ? t('page-api-endpoints:modal.examplePlaceholder', { example: paramDef.example }) : t('page-api-endpoints:modal.enterPlaceholder', { field: paramName })}
                                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                            />
                                                                        </div>
                                                                        <div className="md:col-span-3 text-sm text-gray-500">
                                                                            {paramDef.description || t('page-api-endpoints:modal.pathParameterFallback')}
                                                                            <div className="mt-1 text-xs text-gray-400">
                                                                                {t('page-api-endpoints:modal.pathTypeSuffix', { type: paramDef.type || 'string' })}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}

                                    {/* Query parameters from the endpoint definition */}
                                    {(selectedEndpoint.parameters && Array.isArray(selectedEndpoint.parameters) && selectedEndpoint.parameters.length > 0) ? (
                                        <div>
                                            <h5 className="font-medium text-gray-800 mb-3">{t('page-api-endpoints:modal.queryParameters')}</h5>
                                            <div className="space-y-3">
                                                {(selectedEndpoint.parameters || [])
                                                    .filter(param => {
                                                        // Filter out path parameters to only show query parameters
                                                        if (!selectedEndpoint.path) return true;
                                                        const pathRegex = /{([^}]+)}/g;
                                                        pathRegex.lastIndex = 0; // Reset regex state to ensure consistent matching
                                                        let match;
                                                        while ((match = pathRegex.exec(selectedEndpoint.path)) !== null) {
                                                            if (match[1] === param.name) {
                                                                return false; // Skip if it's a path parameter
                                                            }
                                                        }
                                                        return true; // Include if it's a query parameter
                                                    })
                                                    .filter((param, index, arr) => {
                                                        // Remove duplicates by parameter name
                                                        const firstIndex = arr.findIndex(p => p.name === param.name);
                                                        return firstIndex === index;
                                                    })
                                                    .map((param, idx) => {
                                                        return (
                                                            <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                                <div className="md:col-span-3">
                                                                    <label className="block text-sm font-medium text-gray-700">
                                                                        {param.name}
                                                                        {param.required && <span className="text-red-500"> *</span>}
                                                                    </label>
                                                                </div>
                                                                <div className="md:col-span-6">
                                                                    {param.type === 'boolean' ? (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={testParams[param.name] === true || testParams[param.name] === 'true' || param.default === true}
                                                                            onChange={(e) => handleParamChange(param.name, e.target.checked)}
                                                                            className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                                                                        />
                                                                    ) : param.type === 'date' || param.type === 'datetime' ? (
                                                                        <input
                                                                            type="date"
                                                                            value={testParams[param.name] ?? param.default ?? param.example ?? ''}
                                                                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            value={testParams[param.name] ?? param.default ?? param.example ?? ''}
                                                                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                                                                            placeholder={param.example ? t('page-api-endpoints:modal.examplePlaceholder', { example: param.example }) : t('page-api-endpoints:modal.enterPlaceholder', { field: param.name })}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="md:col-span-3 text-sm text-gray-500">
                                                                    {param.description || t('page-api-endpoints:modal.parameterDescriptionFallback')}
                                                                    <div className="mt-1 text-xs text-gray-400">
                                                                        {t('page-api-endpoints:modal.queryTypeSuffix', { type: param.type || 'string' })}
                                                                        {param.required ? t('page-api-endpoints:modal.requiredSuffix') : t('page-api-endpoints:modal.optionalSuffix')}
                                                                    </div>
                                                                    {param.example && (
                                                                        <div className="mt-1 text-xs text-gray-400">
                                                                            {t('page-api-endpoints:modal.examplePlaceholder', { example: param.example })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    ) : null}

                                    {/* Show message if no parameters exist - check endpoint.parameters */}
                                    {(!selectedEndpoint.parameters || selectedEndpoint.parameters.length === 0) &&
                                     !(selectedEndpoint.path && (() => {
                                         const pathRegex = /{([^}]+)}/g;
                                         pathRegex.lastIndex = 0; // Reset regex state to ensure consistent matching
                                         return pathRegex.test(selectedEndpoint.path);
                                     })()) && (
                                        <p className="text-sm text-gray-500 italic">{t('page-api-endpoints:modal.noParametersRequired')}</p>
                                    )}
                                </div>
                            </div>

                            {/* Display test results if available */}
                            {testResult && (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-gray-900">Results</h4>
                                        <div className="flex space-x-4">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    className="form-radio text-primary-600"
                                                    checked={showResultView === 'table'}
                                                    onChange={() => setShowResultView('table')}
                                                />
                                                <span className="ml-2 text-sm">Table View</span>
                                            </label>
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    className="form-radio text-primary-600"
                                                    checked={showResultView === 'json'}
                                                    onChange={() => setShowResultView('json')}
                                                />
                                                <span className="ml-2 text-sm">JSON View</span>
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
                                        {showResultView === 'table' ? (
                                            <div className="overflow-x-auto">
                                                {testResult.data && Array.isArray(testResult.data) && testResult.data.length > 0 ? (
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                {Object.keys(testResult.data[0]).map(key => (
                                                                    <th key={key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        {key}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {testResult.data.map((item, index) => (
                                                                <tr key={index}>
                                                                    {Object.values(item).map((value, idx) => (
                                                                        <td key={idx} className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-sm text-gray-500">
                                                        {testResult.status === 'success' && (!testResult.data || testResult.data.length === 0) 
                                                            ? 'No data returned from the endpoint.' 
                                                            : 'The response does not contain structured data to display in a table.'}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <pre className="text-xs overflow-auto max-h-96">
                                                {JSON.stringify(testResult, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center mb-4">
                                <button
                                    onClick={() => {
                                        // Clear test results
                                        setTestResult(null);

                                        // Reset all parameter values to defaults
                                        const defaultParams = {};

                                        // Handle path parameters from the endpoint path
                                        if (selectedEndpoint.path) {
                                            const pathRegex = /{([^}]+)}/g;
                                            let match;
                                            pathRegex.lastIndex = 0; // Reset regex state
                                            while ((match = pathRegex.exec(selectedEndpoint.path)) !== null) {
                                                const paramName = match[1];

                                                // Look for this parameter in endpoint parameters to get default value
                                                const paramDef = (selectedEndpoint.parameters || []).find(p => p.name === paramName) || {};

                                                defaultParams[paramName] = paramDef?.default ?? paramDef?.example ?? '';
                                            }
                                        }

                                        // Handle query parameters from endpoint - also check for duplicates
                                        const allParams = [
                                            ...(selectedEndpoint.parameters || [])
                                        ].filter((param, index, arr) => {
                                            // Remove duplicates by parameter name
                                            const firstIndex = arr.findIndex(p => p.name === param.name);
                                            return firstIndex === index;
                                        });

                                        allParams.forEach(param => {
                                            // Only add query parameters (not path parameters) to defaultParams
                                            if (!selectedEndpoint.path || !selectedEndpoint.path.includes(`{${param.name}}`)) {
                                                if (!defaultParams.hasOwnProperty(param.name)) {
                                                    defaultParams[param.name] = param.default ?? param.example ?? '';
                                                }
                                            }
                                        });

                                        setTestParams(defaultParams);
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                >
                                    Clear All
                                </button>
                                <div className="flex gap-2">
                                    <button
                                        onClick={closeTestModal}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleTestEndpoint}
                                        disabled={testingEndpoint}
                                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {testingEndpoint ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Testing...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-4 h-4" />
                                                Test Endpoint
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
